import { useAtom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import styled from 'styled-components'
import iconCloseXGray100 from '~/assets/icons-light-theme/icon-close-x-gray100.svg'
import { StorageKeys } from '~/atoms/StorageKeys'
import { blueToPurpleGradient } from '~/components/CommonStyles'
import { Text } from '~/components/core'
import { ImageIcon } from '~/components/ImageIcon'
import { times } from '~/constants/config'
import { light } from '~/constants/theme'
import { useNotifications } from '~/hooks/useNotifications'
import { BannerProps } from '~/pages/Home/Banners/BannerManager'
import { ApplicationRoutes } from '~/types/RoutingTypes'
import { SettingsType } from '~/types/Settings'

interface WrapperProps {
  show: boolean
  hasJustGranted: boolean
}

const Wrapper = styled.div<WrapperProps>`
  height: ${({ show }) => (show ? '56' : '0')}px;
  padding: 0px 16px;
  display: flex;
  width: 100%;
  justify-content: space-between;
  background-color: ${({ hasJustGranted }) =>
    hasJustGranted ? light.green200 : light.transparent};
  background-image: ${({ hasJustGranted }) =>
    !hasJustGranted && `${blueToPurpleGradient}`};
  align-items: center;
  overflow: hidden;
  transition: all 200ms ease;
`

const SuccessText = styled(Text)`
  color: ${light.gray100};
  display: flex;
  white-space: pre-wrap;
`
interface NotificationsBannerState {
  lastDate?: Date | undefined
}
const notificationsBannerStateAtom = atomWithStorage<NotificationsBannerState>(
  StorageKeys.NotificationsBanner,
  {},
)
const NotificationsBanner = ({ onShowCallback }: BannerProps) => {
  const [notificationsBannerState, setNotificationsBannerState] = useAtom(
    notificationsBannerStateAtom,
  )
  const { hasGranted, requestPermission, sendSampleNotification } =
    useNotifications()
  const navigate = useNavigate()

  const [show, setShow] = useState(false)
  const [hasJustGranted, setHasJustGranted] = useState(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const handleSetShow = (val: boolean) => {
    setShow(val)
    if (val !== show) {
      onShowCallback(val)
    }
  }

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    const shouldShowBanner = () => {
      const lastDate = notificationsBannerState.lastDate

      if (!lastDate && !hasGranted) {
        return true
      }

      const lastDateTimeStamp = lastDate
        ? new Date(lastDate).getTime()
        : new Date().getTime()

      const currentTimeStamp = new Date().getTime()

      const hasBeenDaySinceLastDismiss =
        currentTimeStamp - lastDateTimeStamp > times.dayInMilliseconds

      return hasBeenDaySinceLastDismiss && !hasGranted
    }

    if (shouldShowBanner() && !show) {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      // Set up a new timeout
      timeoutRef.current = setTimeout(() => {
        handleSetShow(true)
        timeoutRef.current = null
      }, 2000)
    }

    // Clean up when dependencies change
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [hasGranted, notificationsBannerState.lastDate, show])

  const dismissBanner = () => {
    setNotificationsBannerState({
      lastDate: new Date(),
    })
    handleSetShow(false)
  }

  const handleRequestPermission = async () => {
    window.gtag('event', 'notification_banner_enable_clicked', {})
    const permission = await requestPermission()

    if (permission == 'granted') {
      window.gtag('event', 'notification_banner_granted', {})
      setHasJustGranted(true)
      sendSampleNotification()
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      // Set up a new timeout for hiding
      timeoutRef.current = setTimeout(() => {
        handleSetShow(false)
        timeoutRef.current = null
      }, 2000)
    } else {
      window.gtag('event', 'notification_banner_blocked', {})
      handleSetShow(false)
    }
  }

  return (
    <Wrapper hasJustGranted={hasJustGranted} show={show}>
      <div style={{ display: 'flex' }}>
        {hasJustGranted ? (
          <SuccessText>
            Fantastic, notification preferences can be adjusted in&nbsp;
            <span
              onClick={() =>
                navigate(
                  `${ApplicationRoutes.SETTINGS}/${SettingsType.NOTIFICATIONS}`,
                )
              }
            >
              <Text
                color={light.gray100}
                style={{
                  display: 'inline-block',
                  textDecoration: 'underline',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                }}
              >
                settings
              </Text>
            </span>
            .
          </SuccessText>
        ) : (
          <>
            <Text color={light.gray100}>
              Mango works best with notifications to keep you up to
              date.&nbsp;{' '}
            </Text>

            <div onClick={() => handleRequestPermission()}>
              <Text
                color={light.gray100}
                style={{
                  fontWeight: 'bold',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
              >
                Enable Notifications!
              </Text>
            </div>
          </>
        )}
      </div>
      <div style={{ cursor: 'pointer' }}>
        <ImageIcon
          onClick={() => dismissBanner()}
          width="20"
          height="20"
          icon={iconCloseXGray100}
        />
      </div>
    </Wrapper>
  )
}

export default NotificationsBanner
